/**
 * Snap-to-road service for the hearth drawing surface (S-04-04).
 *
 * The admin clicks waypoints on the map; this service asks the backend to
 * compute a walking polyline that follows real roads/paths. Per
 * UI-kontrakt vy 7 and `04-lyktan.md` S-04-04 the GraphHopper integration
 * lives on the **backend** as a proxy at `POST /api/admin/route` so the
 * GraphHopper API key never leaves the server. The client therefore never
 * holds a `VITE_GRAPHHOPPER_API_KEY` — the prompt instruction that
 * suggested a client-side env key contradicts UI-kontrakt and was rejected
 * (UI-kontrakt vinner enligt CLAUDE.md källa-prio).
 *
 * Hard rules respected:
 * - No external beacons: this fetches our own backend, not GraphHopper.
 * - No tokens stored client-side: session is the HttpOnly admin cookie set
 *   by S-04-02 (login). We use `credentials: 'include'` so it travels with
 *   the request; we never read or persist it from JS.
 * - Graceful degrade: when the backend is unavailable or returns an error
 *   (e.g. GraphHopper has no route in that area — story acceptance rad
 *   "Tomma rutor … hanteras gracefully") we surface a Swedish user-facing
 *   error string and let the caller fall back to free drawing.
 * - Inputs are validated before we hit the network — saves a round trip
 *   and matches the shared-domain Polyline invariants (>=2 points, valid
 *   WGS84).
 */

import { csrfHeader } from '../auth/csrf';
import type { Coordinate, Polyline } from './types';
import { isValidCoordinate } from './types';
import {
  BATCH_THROTTLE_MS,
  SNAP_MAX_WAYPOINTS_PER_REQUEST,
  batchWaypoints,
  concatPolylines,
  type BatchProgress,
} from './batchedRoute';

export type { BatchProgress } from './batchedRoute';

/** Default backend route endpoint. Same-origin in production, proxied in dev. */
export const DEFAULT_ROUTE_ENDPOINT = '/api/admin/route';

/** Wire shape sent to the backend route proxy. */
export interface RouteRequest {
  readonly waypoints: ReadonlyArray<Coordinate>;
  /**
   * GraphHopper-profil. Vi rullade tillbaka till `foot` 2026-05-04 efter att
   * `hike` (PR #171) gav 404 i prod: GraphHoppers free-tier-konto tillåter
   * bara `[car, bike, foot]` och svarar 400 på alla andra profiler. Backend
   * mappar 400 → NoRoute → 404 till klient, vilket maskerade det som
   * "ingen väg" trots att GraphHopper aldrig fick försöka rutta.
   *
   * Trade-off (samma som motiverade PR #171): `foot` exkluderar vissa
   * `tracks`/informal `paths` i Hisings Backa, så admin kan behöva växla
   * till fri ritning på stigar utanför asfalterade gångvägar. Långsiktigt
   * fix: uppgradera GraphHopper-prenumerationen så `hike` blir tillåten,
   * eller self-hosta GraphHopper med custom_model för urban-foot.
   *
   * Backend-whitelist behåller `hike` för forward-compat — när kontot
   * uppgraderas räcker det att byta strängen här utan deploy av backend.
   */
  readonly profile: 'foot' | 'hike';
  /**
   * Position i ett batchat snap-flöde (S-04-04 follow-up 2026-05-08).
   * 1-indexerat. Skickas alltid (även för enkel-anrop = 1/1) så
   * audit-payloaden får konsistenta fält. Backend tillåter att båda
   * är saknade för bakåtkompatibilitet med eventuella direktanrop.
   */
  readonly batch_index?: number;
  readonly total_batches?: number;
}

/** Successful response from the backend route proxy. */
export interface RouteResponse {
  readonly polyline: Polyline;
}

/**
 * Discriminated result type. Callers branch on `ok` so we never throw
 * across the boundary — a network error or a 5xx is data, not an
 * exception, and the UI must still be able to show the Swedish error
 * string and offer free-draw fallback.
 */
export type RouteResult =
  | { readonly ok: true; readonly polyline: Polyline }
  | { readonly ok: false; readonly userMessage: string; readonly cause: RouteFailureCause };

export type RouteFailureCause =
  | 'invalid-input'
  | 'no-route-found'
  | 'unauthorized'
  | 'rate-limited'
  | 'service-unavailable'
  | 'network-error';

/**
 * Localised user-facing error strings. Swedish per CLAUDE.md user-text
 * rule. Centralised so tests can assert exact wording.
 */
export const ROUTE_ERROR_MESSAGES: Record<RouteFailureCause, string> = {
  'invalid-input':
    'Ogiltiga punkter. Klicka minst två giltiga positioner inom kartan och försök igen.',
  'no-route-found':
    'Kunde inte hitta en väg mellan punkterna. Växla till fri ritning för det här området.',
  unauthorized: 'Sessionen har gått ut. Logga in igen för att fortsätta rita.',
  'rate-limited': 'För många ritförfrågningar samtidigt. Vänta några sekunder och försök igen.',
  'service-unavailable':
    'Snap till väg är otillgängligt just nu. Växla till fri ritning eller försök igen senare.',
  'network-error': 'Kunde inte nå ritservern. Kontrollera nätverket eller växla till fri ritning.',
};

/**
 * Tiny `fetch`-ish abstraction so unit tests can drive the function with a
 * stub instead of patching globals. We keep it minimal — only the fields
 * we actually use.
 */
export interface FetchInit {
  method: string;
  headers: Record<string, string>;
  body: string;
  credentials: 'include';
  signal?: AbortSignal;
}

export interface FetchResponse {
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
}

// eslint-disable-next-line no-unused-vars
export type FetchLike = (input: string, init: FetchInit) => Promise<FetchResponse>;

export interface FetchRouteOptions {
  readonly endpoint?: string;
  readonly fetchImpl?: FetchLike;
  readonly signal?: AbortSignal;
  /**
   * Notifieras före varje GraphHopper-batch när antalet waypoints
   * överskrider [SNAP_MAX_WAYPOINTS_PER_REQUEST] och batchning aktiveras.
   * För enkel-anrop (≤5 waypoints) kallas callbacken en gång med
   * `{batchIndex: 1, totalBatches: 1}`. UI:t använder den för "Snappar
   * punkt X av Y"-text.
   */
  readonly onProgress?: (progress: BatchProgress) => void;
  /**
   * Övre tak för paus (ms) mellan batch-anrop. Default kommer från
   * [BATCH_THROTTLE_MS] (1500ms) som matchar GraphHopper free-tiers
   * minute-rate-limit. Lägre värden är OK i tester.
   */
  readonly batchThrottleMs?: number;
}

/**
 * Validate caller-supplied waypoints before issuing a network request.
 * Returns a `RouteResult` directly so the caller can branch uniformly.
 */
export function validateWaypoints(waypoints: ReadonlyArray<Coordinate>): RouteResult | null {
  if (waypoints.length < 2) {
    return {
      ok: false,
      userMessage: ROUTE_ERROR_MESSAGES['invalid-input'],
      cause: 'invalid-input',
    };
  }
  for (const w of waypoints) {
    if (!isValidCoordinate(w)) {
      return {
        ok: false,
        userMessage: ROUTE_ERROR_MESSAGES['invalid-input'],
        cause: 'invalid-input',
      };
    }
  }
  return null;
}

/**
 * Map an HTTP status to a stable `RouteFailureCause`. The backend
 * contract (S-04-04 server-side, future story) will use these statuses
 * deliberately:
 *  - 400: validation or empty route from GraphHopper
 *  - 401/403: session expired or missing admin role
 *  - 404: backend reports "no path between waypoints"
 *  - 429: rate-limit
 *  - 5xx or 502/503: GraphHopper or backend unhealthy
 */
function classifyHttpStatus(status: number): RouteFailureCause {
  if (status === 400) return 'invalid-input';
  if (status === 401 || status === 403) return 'unauthorized';
  if (status === 404) return 'no-route-found';
  if (status === 429) return 'rate-limited';
  return 'service-unavailable';
}

/**
 * Ask the backend route proxy for a snap-to-road polyline between
 * `waypoints`. Returns a discriminated result; never throws unless the
 * caller passes an already-aborted signal (which is treated as a
 * programming error and surfaces as a normal AbortError on `fetch`).
 *
 * **Batchning (S-04-04 follow-up 2026-05-08):** GraphHopper free-tier
 * tillåter max 5 waypoints/request. När `waypoints.length > 5` delas
 * input upp i batcher om 5 punkter med 1 punkts overlap, anropen
 * sequenseras med [BATCH_THROTTLE_MS] mellan, och resulterande polylines
 * sätts ihop till en sammanhängande linje. Empirisk verifiering på
 * riktig användardata 2026-05-08 visade ≤2% överlängd jämfört med
 * decimering (1.3% på 30-punkts-runda). Vid en enskild batch-fail
 * returneras felet utan retry — caller (HearthsView) faller tillbaka
 * till raka linjer.
 *
 * Wire contract per anrop:
 * Request body:  `{"waypoints":[{"latitude":..,"longitude":..},...],"profile":"foot"}`
 * Response 200:  `{"polyline":{"points":[{"latitude":..,"longitude":..},...]}}`
 */
export async function fetchRoute(
  waypoints: ReadonlyArray<Coordinate>,
  options: FetchRouteOptions = {},
): Promise<RouteResult> {
  const validationFailure = validateWaypoints(waypoints);
  if (validationFailure) return validationFailure;

  if (waypoints.length <= SNAP_MAX_WAYPOINTS_PER_REQUEST) {
    options.onProgress?.({ batchIndex: 1, totalBatches: 1 });
    return fetchRouteSingle(waypoints, 1, 1, options);
  }

  const batches = batchWaypoints(waypoints);
  const polylines: Polyline[] = [];
  const throttleMs = options.batchThrottleMs ?? BATCH_THROTTLE_MS;

  for (let i = 0; i < batches.length; i++) {
    const batchIndex = i + 1;
    options.onProgress?.({ batchIndex, totalBatches: batches.length });
    const result = await fetchRouteSingle(batches[i], batchIndex, batches.length, options);
    if (!result.ok) {
      // Fail fast vid en batchs misslyckande — UI:t visar felmeddelandet
      // och faller tillbaka till raka linjer (samma fallback som vid
      // single-anrop). Retry med exponential backoff är overkill för
      // pilot — användaren kan klicka "Försök igen" själv.
      return result;
    }
    polylines.push(result.polyline);
    if (i < batches.length - 1 && throttleMs > 0) {
      await sleep(throttleMs);
    }
  }

  return { ok: true, polyline: concatPolylines(polylines) };
}

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * Internt enkel-anrop mot backend-route-proxyn. Antar att caller redan
 * har validerat input (≥2 punkter, WGS84) — `fetchRoute()` gör det på
 * hela inputen innan batchning, inte per batch.
 */
async function fetchRouteSingle(
  waypoints: ReadonlyArray<Coordinate>,
  batchIndex: number,
  totalBatches: number,
  options: FetchRouteOptions,
): Promise<RouteResult> {
  const endpoint = options.endpoint ?? DEFAULT_ROUTE_ENDPOINT;
  const fetchImpl: FetchLike =
    options.fetchImpl ?? (globalThis as unknown as { fetch: FetchLike }).fetch;

  const body: RouteRequest = {
    waypoints,
    profile: 'foot',
    batch_index: batchIndex,
    total_batches: totalBatches,
  };

  let response: Awaited<ReturnType<FetchLike>>;
  try {
    response = await fetchImpl(endpoint, {
      method: 'POST',
      // CSRF-skydd (S-12-11c): X-CSRF-Token-headern speglar värdet från
      // harden_admin_csrf-cookien som backend satt vid login. Saknas
      // cookien skickas headern inte alls — backend svarar 403 vilket
      // mappas till `unauthorized` och UI:t visar "Sessionen har gått
      // ut". Det är rätt UX när sessionen expirerat.
      headers: { 'Content-Type': 'application/json', ...csrfHeader() },
      body: JSON.stringify(body),
      credentials: 'include',
      signal: options.signal,
    });
  } catch {
    return {
      ok: false,
      userMessage: ROUTE_ERROR_MESSAGES['network-error'],
      cause: 'network-error',
    };
  }

  if (!response.ok) {
    const cause = classifyHttpStatus(response.status);
    return { ok: false, userMessage: ROUTE_ERROR_MESSAGES[cause], cause };
  }

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    return {
      ok: false,
      userMessage: ROUTE_ERROR_MESSAGES['service-unavailable'],
      cause: 'service-unavailable',
    };
  }

  const polyline = readPolyline(parsed);
  if (!polyline) {
    return {
      ok: false,
      userMessage: ROUTE_ERROR_MESSAGES['no-route-found'],
      cause: 'no-route-found',
    };
  }

  return { ok: true, polyline };
}

/**
 * Defensive parser: the response is JSON but we never trust shape.
 * Returns null when the payload doesn't look like a valid polyline.
 */
function readPolyline(raw: unknown): Polyline | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as { polyline?: unknown };
  if (!obj.polyline || typeof obj.polyline !== 'object') return null;
  const inner = obj.polyline as { points?: unknown };
  if (!Array.isArray(inner.points)) return null;

  const points: Coordinate[] = [];
  for (const candidate of inner.points) {
    if (!candidate || typeof candidate !== 'object') return null;
    const p = candidate as { latitude?: unknown; longitude?: unknown };
    if (typeof p.latitude !== 'number' || typeof p.longitude !== 'number') return null;
    const c: Coordinate = { latitude: p.latitude, longitude: p.longitude };
    if (!isValidCoordinate(c)) return null;
    points.push(c);
  }
  if (points.length < 2) return null;
  return { points };
}
